import { Box, RadioCard, RadioCardGroup, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import type { RefObject } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { TWorkerTypeChoice } from '~/domain/SaveWorkerType';
import { AuthErrorBanner } from '../../../shared';
import type { ISignUpFormValues } from '../../types';
import './WorkerTypeSelection.css';

export interface IWorkerTypeSelectionProps {
  bannerVisible: boolean;
  bannerRef: RefObject<HTMLDivElement | null>;
}

const OPTIONS: Array<{
  value: TWorkerTypeChoice;
  label: string;
  topLine: string;
  pricing?: string;
}> = [
  {
    value: 'contractor',
    label: 'Contractor option',
    topLine: 'Independent contractors offering services through GoTu',
    pricing: 'Only pay the GoTu Service Fee',
  },
  {
    value: 'employee',
    label: 'Employee option',
    topLine: 'Employees of GoTu’s PEO sent to your office',
    pricing: 'Pay the GoTu Service Fee + Employee Fee (20%)',
  },
  {
    value: 'none',
    label: 'I’m not sure. I’ll decide later',
    topLine: 'You can set this later, but won’t be able to post shifts until decided',
  },
];

export const WorkerTypeSelection = ({ bannerVisible, bannerRef }: IWorkerTypeSelectionProps) => {
  const { control } = useFormContext<ISignUpFormValues>();

  return (
    <Box>
      <Typography
        as="h2"
        bold
        color={colors.primary[500]}
        textAlign="center"
        style={{ fontSize: 22, margin: 0 }}
      >
        Worker Type Selection
      </Typography>
      <Typography
        as="p"
        bold
        color={colors.neutral[600]}
        textAlign="center"
        style={{ fontSize: 18, margin: 0, marginTop: 24, marginBottom: bannerVisible ? 8 : 36 }}
      >
        Please select a Worker Type for this Office:
      </Typography>
      {bannerVisible && (
        <Box ref={bannerRef} style={{ marginBottom: 4 }}>
          <AuthErrorBanner visible={bannerVisible} />
        </Box>
      )}
      <Controller
        name="workerType"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <RadioCardGroup
            value={field.value}
            onChange={field.onChange}
            error={Boolean(fieldState.error)}
            orientation="vertical"
            style={{
              gap: 16,
            }}
          >
            {OPTIONS.map(({ value, label, topLine, pricing }) => (
              <RadioCard
                key={value}
                data-cy={`radio-worker-type-${value}`}
                value={value}
                label={label}
                className="worker-type-radio-card"
                style={{
                  paddingLeft: 36,
                  paddingTop: 24,
                }}
              >
                <Typography size="body_large" color={colors.neutral[500]}>
                  {topLine}
                </Typography>
                {pricing && (
                  <Typography as="span" color={colors.primary[500]} italic>
                    {pricing}
                  </Typography>
                )}
              </RadioCard>
            ))}
          </RadioCardGroup>
        )}
      />
    </Box>
  );
};
